import React, { useEffect, useState } from 'react';
import { Loader2, Pencil, Trash2 } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { useToast } from '../../hooks/use-toast';
import { clientService } from '../../api';

const TYPES = ['Letter', 'Verification', 'Audit', 'Objection', 'Other'];
const STATUSES = { open: 'Open', responded: 'Responded', closed: 'Closed' };
const STATUS_CLS = { open: 'bg-amber-50 text-amber-800', responded: 'bg-sky-50 text-sky-800', closed: 'bg-stone-100 text-stone-600' };
const toInput = (iso) => (iso ? new Date(iso).toISOString().slice(0, 10) : '');
const toIso = (d) => (d ? new Date(`${d}T09:00:00Z`).toISOString() : null);
const EMPTY = { clientId: '', type: 'Letter', title: '', reference: '', receivedDate: '', responseDue: '', status: 'open', notes: '' };

export const SarsDialog = ({ open, onOpenChange, item, clients, fixedClientId, onSaved }) => {
  const { toast } = useToast();
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!open) return;
    setForm(item ? { ...EMPTY, ...item, receivedDate: toInput(item.receivedDate), responseDue: toInput(item.responseDue) } : { ...EMPTY, clientId: fixedClientId || '' });
  }, [open, item, fixedClientId]);
  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = (e) => {
    e.preventDefault();
    if (!form.clientId) { toast({ title: 'Pick a client', variant: 'destructive' }); return; }
    setBusy(true);
    const body = { clientId: form.clientId, type: form.type, title: form.title, reference: form.reference, status: form.status, notes: form.notes,
      receivedDate: toIso(form.receivedDate), responseDue: toIso(form.responseDue) };
    (item ? clientService.updateSars(item.id, body) : clientService.createSars(body))
      .then(() => { toast({ title: item ? 'SARS item updated' : 'SARS item logged', description: body.responseDue ? 'Response deadline added to Tasks.' : undefined }); onOpenChange(false); onSaved(); })
      .catch((err) => toast({ title: 'Save failed', description: err?.response?.data?.detail || 'Try again.', variant: 'destructive' }))
      .finally(() => setBusy(false));
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader><DialogTitle>{item ? 'Edit SARS correspondence' : 'Log SARS correspondence'}</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          {!fixedClientId && (
            <div><Label>Client</Label>
              <Select value={form.clientId || undefined} onValueChange={set('clientId')}>
                <SelectTrigger className="mt-1.5" data-testid="sars-client-select"><SelectValue placeholder="Select client"/></SelectTrigger>
                <SelectContent>{clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          )}
          <div className="grid grid-cols-2 gap-4">
            <div><Label>Type</Label>
              <Select value={form.type} onValueChange={set('type')}>
                <SelectTrigger className="mt-1.5" data-testid="sars-type-select"><SelectValue/></SelectTrigger>
                <SelectContent>{TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Status</Label>
              <Select value={form.status} onValueChange={set('status')}>
                <SelectTrigger className="mt-1.5" data-testid="sars-status-select"><SelectValue/></SelectTrigger>
                <SelectContent>{Object.entries(STATUSES).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <div><Label htmlFor="sars-title">Subject</Label><Input id="sars-title" required value={form.title} onChange={(e) => set('title')(e.target.value)} placeholder="VAT201 verification — Aug 2026" className="mt-1.5" data-testid="sars-title-input"/></div>
          <div><Label htmlFor="sars-ref">SARS case / reference number</Label><Input id="sars-ref" value={form.reference} onChange={(e) => set('reference')(e.target.value)} className="mt-1.5" data-testid="sars-reference-input"/></div>
          <div className="grid grid-cols-2 gap-4">
            <div><Label htmlFor="sars-rec">Received</Label><Input id="sars-rec" type="date" value={form.receivedDate} onChange={(e) => set('receivedDate')(e.target.value)} className="mt-1.5" data-testid="sars-received-input"/></div>
            <div><Label htmlFor="sars-due">Response due</Label><Input id="sars-due" type="date" value={form.responseDue} onChange={(e) => set('responseDue')(e.target.value)} className="mt-1.5" data-testid="sars-due-input"/></div>
          </div>
          <div><Label htmlFor="sars-notes">Notes</Label><Textarea id="sars-notes" value={form.notes} onChange={(e) => set('notes')(e.target.value)} className="mt-1.5" data-testid="sars-notes-input"/></div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button type="submit" disabled={busy} className="bg-emerald-800 hover:bg-emerald-900 text-white" data-testid="sars-save-btn">{busy && <Loader2 size={14} className="mr-2 animate-spin"/>} Save</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

const dueCell = (s) => {
  if (!s.responseDue) return <span className="text-stone-400">—</span>;
  const d = new Date(s.responseDue);
  const days = Math.ceil((d - Date.now()) / 86400000);
  const late = s.status === 'open' && days < 0;
  return (
    <div className={late ? 'text-red-700 font-medium' : s.status === 'open' && days <= 5 ? 'text-amber-700 font-medium' : 'text-stone-700'}>
      {d.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' })}
      {s.status === 'open' && <div className="text-[11px] font-normal text-stone-500">{late ? `${-days}d overdue` : `${days}d left`}</div>}
    </div>
  );
};

export const SarsTable = ({ items, showClient, onEdit, onDelete }) => (
  <div className="overflow-x-auto">
    <table className="w-full text-sm" data-testid="sars-table">
      <thead className="bg-stone-50 text-stone-600 text-xs uppercase tracking-wider">
        <tr>
          {showClient && <th className="text-left px-5 py-3 font-medium">Client</th>}
          <th className="text-left px-5 py-3 font-medium">Type</th>
          <th className="text-left px-5 py-3 font-medium">Subject</th>
          <th className="text-left px-5 py-3 font-medium">Reference</th>
          <th className="text-left px-5 py-3 font-medium">Response due</th>
          <th className="text-left px-5 py-3 font-medium">Status</th>
          <th className="px-5 py-3"/>
        </tr>
      </thead>
      <tbody className="divide-y divide-stone-100">
        {items.length === 0 && <tr><td colSpan={7} className="px-5 py-10 text-center text-stone-500" data-testid="sars-empty">No SARS correspondence logged.</td></tr>}
        {items.map((s) => (
          <tr key={s.id} className="hover:bg-stone-50" data-testid={`sars-row-${s.id}`}>
            {showClient && <td className="px-5 py-3 font-medium text-stone-900">{s.clientName}</td>}
            <td className="px-5 py-3 text-stone-700">{s.type}</td>
            <td className="px-5 py-3 text-stone-900">{s.title}{s.notes && <div className="text-xs text-stone-500 truncate max-w-xs">{s.notes}</div>}</td>
            <td className="px-5 py-3 font-mono text-xs text-stone-600">{s.reference || '—'}</td>
            <td className="px-5 py-3">{dueCell(s)}</td>
            <td className="px-5 py-3"><span className={`text-xs font-medium px-2 py-0.5 rounded-full ${STATUS_CLS[s.status]}`}>{STATUSES[s.status]}</span></td>
            <td className="px-5 py-3 text-right whitespace-nowrap">
              <button type="button" onClick={() => onEdit(s)} className="p-1 text-stone-500 hover:text-emerald-800" data-testid={`sars-edit-${s.id}`}><Pencil size={15}/></button>
              <button type="button" onClick={() => onDelete(s)} className="p-1 text-stone-500 hover:text-red-600" data-testid={`sars-delete-${s.id}`}><Trash2 size={15}/></button>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

export const useSarsActions = (reload) => {
  const { toast } = useToast();
  const [dialog, setDialog] = useState({ open: false, item: null });
  const remove = (s) => { if (window.confirm(`Delete "${s.title}"? Its open task will also be removed.`)) clientService.deleteSars(s.id).then(() => { toast({ title: 'SARS item deleted' }); reload(); }); };
  return { dialog, setDialog, remove, openNew: () => setDialog({ open: true, item: null }), openEdit: (item) => setDialog({ open: true, item }) };
};
